/* ---- WebView-SDK Bridge (SpaceTimeAuth) ----
 * Abstracts iOS (WKScriptMessageHandler) and Android (JavascriptInterface) differences.
 * Falls back to mock data when running in a regular browser.
 *
 * Bridge object names:
 *   StaAndroid  — JS→SDK sync queries   (window.StaAndroid.getUuid() etc.)
 *   StaBridge   — SDK→JS async callbacks (window.StaBridge.onLocationUpdate(json) etc.)
 */

import { MOCK_BASE_LOCATION, MOCK_SPOOFING_STATUS, generateMockLocation } from '../mocks/location';
import type { Geofence, GeofenceEvent, LocationData, SpoofingStatus } from '../types';

/** Callback handlers registered by the app */
export interface BridgeCallbacks {
  onLocationUpdate?: (location: LocationData) => void;
  onSpoofingStatus?: (status: SpoofingStatus) => void;
  onGeofenceEvent?: (event: GeofenceEvent) => void;
  onError?: (error: { code: string; message: string }) => void;
  onLocationContext?: (data: {
    current_geofences: {
      id: string;
      name: string;
      category: string;
      coordinates?: number[][];
      in_schedule?: boolean;
    }[];
    all_geofences: {
      id: string;
      name: string;
      category: string;
      coordinates?: number[][];
      in_schedule?: boolean;
    }[];
    address: string | null;
    address_confidence: number;
    address_candidates: { address: string; probability: number; key_code: string }[];
    geofence_candidates: {
      name: string;
      probability: number;
      key_code: string;
      in_schedule?: boolean;
    }[];
    /** Phase 2-b: 環境推定 (INDOOR / OUTDOOR / UNKNOWN) */
    environment?: string;
    /** Phase 2-b: 環境推定信頼度 (HIGH / MEDIUM / LOW) */
    environment_accuracy?: string;
  }) => void;
  /** Google Sign-In 成功時 (Host app の OAuth フロー完了通知) */
  onGoogleSignIn?: (data: { id_token: string; email: string; name: string }) => void;
  /** Google Sign-In 失敗時 */
  onGoogleSignInError?: (data: { code: string; message: string }) => void;
}

type Platform = 'ios' | 'android' | 'browser';

/** Window augmentation for native bridges */
declare global {
  interface Window {
    // iOS (WKScriptMessageHandler)
    webkit?: {
      messageHandlers: {
        requestLocation?: { postMessage: (data: unknown) => void };
        setGeofences?: { postMessage: (data: unknown) => void };
        startMonitoring?: { postMessage: (data: unknown) => void };
        stopMonitoring?: { postMessage: (data: unknown) => void };
        refreshGeofences?: { postMessage: (data: unknown) => void };
        signInWithGoogle?: { postMessage: (data: unknown) => void };
      };
    };
    // Android (@JavascriptInterface) — JS→SDK
    StaAndroid?: {
      getUuid: () => string;
      getPlatform: () => string;
      getSdkVersion: () => string;
      isInitialized: () => boolean;
      requestLocation: (data: string) => void;
      setGeofences: (data: string) => void;
      startMonitoring: (data: string) => void;
      stopMonitoring: (data: string) => void;
      refreshGeofences: (data: string) => void;
      /** Host app の OAuth 実装 (DemoStaWebView) を起動 */
      signInWithGoogle: (data: string) => void;
    };
    // SDK→JS callbacks
    StaBridge: {
      onLocationUpdate: (json: string) => void;
      onSpoofingStatus: (json: string) => void;
      onGeofenceEvent: (json: string) => void;
      onError: (json: string) => void;
      onLocationContext: (json: string) => void;
      onGoogleSignIn: (json: string) => void;
      onGoogleSignInError: (json: string) => void;
    };
    /** iOS only: MainViewController が起動時に evaluateJavaScript で inject する device UUID */
    __staDeviceUuid?: string;
  }
}

class SdkBridge {
  private platform: Platform;
  private callbacks: BridgeCallbacks = {};
  private mockTimerId: number | null = null;

  constructor() {
    this.platform = this.detectPlatform();
    this.registerGlobalCallbacks();
  }

  private detectPlatform(): Platform {
    if (window.webkit?.messageHandlers?.requestLocation) return 'ios';
    if (window.StaAndroid) return 'android';
    return 'browser';
  }

  getPlatform(): Platform {
    return this.platform;
  }

  /** Get device UUID from native SDK */
  getUuid(): string | null {
    if (this.platform === 'android') {
      return window.StaAndroid!.getUuid();
    }
    // iOS は MainViewController が evaluateJavaScript で window.__staDeviceUuid に inject (タイミング依存で未定義あり)
    if (this.platform === 'ios') {
      return window.__staDeviceUuid ?? null;
    }
    return null;
  }

  /** Get SDK version from native SDK */
  getSdkVersion(): string | null {
    if (this.platform === 'android') {
      return window.StaAndroid!.getSdkVersion();
    }
    return null;
  }

  /** Check if native SDK is initialized */
  isInitialized(): boolean {
    if (this.platform === 'android') {
      return window.StaAndroid!.isInitialized();
    }
    return false;
  }

  /** Register callback handlers */
  setCallbacks(cb: BridgeCallbacks): void {
    this.callbacks = { ...this.callbacks, ...cb };
  }

  /** Request current location from SDK */
  requestLocation(): void {
    if (this.platform === 'ios') {
      window.webkit!.messageHandlers.requestLocation!.postMessage({});
    } else if (this.platform === 'android') {
      window.StaAndroid!.requestLocation('{}');
    } else {
      this.mockLocationUpdate();
    }
  }

  /** Send geofence definitions to SDK */
  setGeofences(geofences: Geofence[]): void {
    const data = geofences.map((g) => ({
      id: g.id,
      name: g.name,
      coordinates: g.coordinates,
    }));
    if (this.platform === 'ios') {
      window.webkit!.messageHandlers.setGeofences!.postMessage(data);
    } else if (this.platform === 'android') {
      window.StaAndroid!.setGeofences(JSON.stringify(data));
    }
    // browser: no-op
  }

  /** Start geofence monitoring */
  startMonitoring(): void {
    if (this.platform === 'ios') {
      window.webkit!.messageHandlers.startMonitoring!.postMessage({});
    } else if (this.platform === 'android') {
      window.StaAndroid!.startMonitoring('{}');
    } else {
      this.startMockMonitoring();
    }
  }

  /** Stop geofence monitoring */
  stopMonitoring(): void {
    if (this.platform === 'ios') {
      window.webkit!.messageHandlers.stopMonitoring!.postMessage({});
    } else if (this.platform === 'android') {
      window.StaAndroid!.stopMonitoring('{}');
    } else {
      this.stopMockMonitoring();
    }
  }

  /**
   * Request immediate geofence definition refresh from server.
   * SDK は通常 5 分間隔で自動再取得するが、admin が定義を編集した直後の
   * 即時反映用に手動更新ボタンから呼ぶ。
   * Android: SDK 内部の `_geofences` を更新後、`onLocationContext` で WebView に push される。
   * iOS: 現状 SDK の GeofenceMonitor が skeleton のため bridge は no-op (P3-iOS 完了後に有効化)。
   * Browser: no-op。caller は `eventService.listGeofences()` で fallback すること。
   */
  refreshGeofences(): void {
    if (this.platform === 'ios') {
      window.webkit!.messageHandlers.refreshGeofences?.postMessage({});
    } else if (this.platform === 'android') {
      window.StaAndroid!.refreshGeofences('{}');
    }
  }

  /**
   * Request native Google Sign-In flow.
   * Android: `StaAndroid.signInWithGoogle` (GoogleSignIn API).
   * iOS: WebKit message handler `signInWithGoogle` → demo App が
   *      `ASWebAuthenticationSession` で OAuth 2.0 implicit flow を起動。
   * 完了通知は `onGoogleSignIn` / `onGoogleSignInError` コールバックで JS に返る。
   */
  signInWithGoogle(): void {
    if (this.platform === 'ios') {
      window.webkit!.messageHandlers.signInWithGoogle?.postMessage({});
    } else if (this.platform === 'android') {
      window.StaAndroid!.signInWithGoogle('{}');
    }
    // browser: no-op (caller がエラー表示を担当)
  }

  /** Register global callbacks for SDK -> WebView communication */
  private registerGlobalCallbacks(): void {
    window.StaBridge = {
      onLocationUpdate: (json: string) => {
        try {
          const data: LocationData = JSON.parse(json);
          this.callbacks.onLocationUpdate?.(data);
        } catch {
          /* ignore parse errors */
        }
      },
      onSpoofingStatus: (json: string) => {
        try {
          const data: SpoofingStatus = JSON.parse(json);
          this.callbacks.onSpoofingStatus?.(data);
        } catch {
          /* ignore */
        }
      },
      onGeofenceEvent: (json: string) => {
        try {
          const data: GeofenceEvent = JSON.parse(json);
          this.callbacks.onGeofenceEvent?.(data);
        } catch {
          /* ignore */
        }
      },
      onError: (json: string) => {
        try {
          const data = JSON.parse(json);
          this.callbacks.onError?.(data);
        } catch {
          /* ignore */
        }
      },
      onLocationContext: (json: string) => {
        try {
          const data = JSON.parse(json);
          this.callbacks.onLocationContext?.(data);
        } catch {
          /* ignore */
        }
      },
      onGoogleSignIn: (json: string) => {
        try {
          const data = JSON.parse(json);
          this.callbacks.onGoogleSignIn?.(data);
        } catch {
          /* ignore */
        }
      },
      onGoogleSignInError: (json: string) => {
        try {
          const data = JSON.parse(json);
          this.callbacks.onGoogleSignInError?.(data);
        } catch {
          /* ignore */
        }
      },
    };
  }

  /* ---- Mock helpers for browser testing ---- */

  private mockLocationUpdate(): void {
    const loc: LocationData = { ...MOCK_BASE_LOCATION, timestamp: new Date().toISOString() };
    setTimeout(() => {
      this.callbacks.onLocationUpdate?.(loc);
    }, 100);
  }

  private startMockMonitoring(): void {
    if (this.mockTimerId !== null) return;
    // Send periodic mock location updates
    this.mockTimerId = window.setInterval(() => {
      this.callbacks.onLocationUpdate?.(generateMockLocation());
      this.callbacks.onSpoofingStatus?.(MOCK_SPOOFING_STATUS);
    }, 5000);

    // Initial update
    this.mockLocationUpdate();
    setTimeout(() => {
      this.callbacks.onSpoofingStatus?.(MOCK_SPOOFING_STATUS);
    }, 200);
  }

  private stopMockMonitoring(): void {
    if (this.mockTimerId !== null) {
      clearInterval(this.mockTimerId);
      this.mockTimerId = null;
    }
  }
}

export const sdkBridge = new SdkBridge();
